import { useState, useCallback } from 'react';
import useAuth from './useAuth';
import useCallApiAndLoad from './useCallApiAndLoad';
import { initChatEndpoint, sendChatMessageEndpoint } from '../services/chatService';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

const useChat = () => {
  const { getAuthToken } = useAuth();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [chatContext, setChatContext] = useState<{
    property_code: string;
    marketplace_type: string;
  } | null>(null);

  const initChat = useCallback(
    async (propertyCode: string, marketplaceType: string) => {
      if (isInitialized || !propertyCode) return;
      const token = getAuthToken() ?? '';
      const response = await callEndpoint(
        initChatEndpoint(propertyCode, marketplaceType, token),
      );
      if (response?.status >= 200 && response?.status < 300) {
        const replyText = response.data?.message ?? 'Hola, ¿en qué puedo ayudarte?';
        setMessages([{ id: `b-${Date.now()}`, role: 'assistant', text: String(replyText) }]);
        setChatContext({ property_code: propertyCode, marketplace_type: marketplaceType });
        setIsInitialized(true);
      } else {
        setMessages([
          { id: `e-${Date.now()}`, role: 'assistant', text: 'Error al inicializar el asistente.' },
        ]);
      }
    },
    [isInitialized, getAuthToken, callEndpoint],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', text: text.trim() };
      setMessages((prev) => [...prev, userMsg]);
      const token = getAuthToken() ?? '';
      const response = await callEndpoint(
        sendChatMessageEndpoint(
          text.trim(),
          chatContext?.property_code ?? '',
          chatContext?.marketplace_type ?? '',
          token,
        ),
      );
      const replyText =
        response?.status >= 200 && response?.status < 300
          ? (response.data?.message ?? 'Sin respuesta')
          : 'Error al conectar con el asistente.';
      setMessages((prev) => [
        ...prev,
        { id: `b-${Date.now()}`, role: 'assistant', text: String(replyText) },
      ]);
    },
    [getAuthToken, callEndpoint, chatContext],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setIsInitialized(false);
    setChatContext(null);
  }, []);

  return { messages, isLoading, isInitialized, sendMessage, initChat, clearMessages };
};

export default useChat;
